import styles from './DavidPifView.module.css'
import { useElementOutOfView } from '@/hooks/useElementOutOfView'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { Faq } from '@/types/codegen-federation'
import { PayItForwardCheckoutProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/usePayItForwardState'
import { ClaimTicketSection } from './components/ClaimTicketSection'
import { DavidEpisodesSection } from './components/DavidEpisodesSection'
import { DavidGoliathSection } from './components/DavidGoliathSection'
import { EndCtaSection } from './components/EndCtaSection'
import { FaqSection } from './components/FaqSection'
import { HelpLightSection } from './components/HelpLightSection'
import { Hero } from './components/Hero'
import { HowItWorksSection } from './components/HowItWorksSection'
import { ImgFullParallax } from './components/ImgFullParallax'
import { InspireGenSection } from './components/InspireGenSection'
import { PifOverlay } from './components/PifOverlay'
import { ShareDavidCta } from './components/ShareDavidCta'
import { SpecialMessageSection } from './components/SpecialMessageSection'
import { StoriesSongsSection } from './components/StoriesSongsSection'
import { SuperheroesSection } from './components/SuperheroesSection'
import { PifOverlayProvider, usePifOverlay } from './contexts/PifOverlayContext'

interface DavidPifViewProps {
  theatricalRelease: TheatricalReleaseResult
  davidInlinePifPaymentFormEnabled?: boolean
  davidEpisodesExperimentEnabled?: boolean
  davidDollarVsPeopleExperimentEnabled?: boolean
}

function DavidPifViewContent({
  theatricalRelease,
  davidInlinePifPaymentFormEnabled,
  davidEpisodesExperimentEnabled = false,
  davidDollarVsPeopleExperimentEnabled = false,
}: DavidPifViewProps) {
  const [heroRef, isHeroOutOfView] = useElementOutOfView<HTMLElement>()
  const [helpLightRef, hasReachedHelpLight] = useIntersectionObserver<HTMLElement>({
    rootMargin: '0px',
    triggerOnce: true,
  })
  const [faqRef, isFaqVisible] = useIntersectionObserver<HTMLElement>({
    rootMargin: '0px',
  })
  const { isOpen: isOverlayOpen } = usePifOverlay()
  const releaseDate = theatricalRelease?.region?.releaseDate

  const showShareCta = isHeroOutOfView && hasReachedHelpLight && !isOverlayOpen && !isFaqVisible

  return (
    <div className={`david-pif-view relative min-h-screen w-full overflow-x-hidden bg-[#141447] ${styles.container}`}>
      <ShareDavidCta show={showShareCta} />

      <main className="relative">
        <Hero ref={heroRef} />
        <HelpLightSection ref={helpLightRef} releaseDate={releaseDate} />
        <ClaimTicketSection />
        <InspireGenSection />
        {davidEpisodesExperimentEnabled && <DavidEpisodesSection />}
        <SpecialMessageSection davidEpisodesExperimentEnabled={davidEpisodesExperimentEnabled} />
        <SuperheroesSection />
        <StoriesSongsSection />
        <DavidGoliathSection />
        <HowItWorksSection />
        <EndCtaSection releaseDate={releaseDate} />
        <ImgFullParallax />
        <FaqSection ref={faqRef} faqs={theatricalRelease?.region?.faqs as Faq[]} />
      </main>

      <PifOverlay
        releaseDate={releaseDate}
        projectSlug="david"
        paymentInlineExperimentEnabled={davidInlinePifPaymentFormEnabled}
        davidEpisodesExperimentEnabled={davidEpisodesExperimentEnabled}
        davidDollarVsPeopleExperimentEnabled={davidDollarVsPeopleExperimentEnabled}
      />
    </div>
  )
}

export function DavidPifView(props: DavidPifViewProps) {
  return (
    <PayItForwardCheckoutProvider>
      <PifOverlayProvider>
        <DavidPifViewContent {...props} />
      </PifOverlayProvider>
    </PayItForwardCheckoutProvider>
  )
}
